import React from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, MapPin, Trees } from "lucide-react";
import { PHONE, PHONE_TEL, HERO_VIDEO_URL } from "../lib/utils";
import { Splat } from "./Splat";
import PreReservationDialog from "./PreReservationDialog";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 overflow-hidden bg-white"
    >
      {/* Decorative splatters */}
      <Splat
        color="#FFEB3B"
        className="absolute -top-10 -left-16 w-72 h-72 opacity-90 animate-splat-spin"
      />
      <Splat
        color="#00E676"
        className="absolute top-40 right-0 w-40 h-40 opacity-80 animate-splat-spin"
        style={{ animationDirection: "reverse" }}
      />
      <Splat
        color="#00BCD4"
        className="absolute bottom-10 left-10 w-24 h-24 opacity-70"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-8 lg:gap-10 py-8 md:py-12 lg:py-16">
        {/* Left: copy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 flex flex-col justify-center"
        >
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 self-start px-3 py-1.5 bg-yellow-300 text-black font-mono text-xs font-bold uppercase tracking-[0.2em] mb-6 border-2 border-black"
          >
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={3} />
            Sur réservation uniquement
          </div>

          <h1
            data-testid="hero-h1"
            aria-label="Paintball Aventures 35 — vos sessions à Fougères et Louvigné-du-Désert"
            className="font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[5rem] xl:text-[5.75rem] font-bold uppercase leading-[0.9] tracking-tighter text-black"
          >
            <span className="block">Paintball </span>
            <span className="block text-[#E91E63]">Aventures 35</span>
            <span className="block mt-4 text-xl sm:text-2xl md:text-3xl lg:text-[2rem] xl:text-4xl text-black/90 font-bold leading-[1.05]">
              Vos sessions à{" "}
              <span className="drip-underline">Fougères</span> &amp;{" "}
              <span className="drip-underline">Louvigné-du-Désert</span>
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 md:mt-8 text-base md:text-lg text-gray-700 max-w-2xl leading-relaxed"
          >
            Vivez une expérience <strong>immersive en pleine nature</strong> pour vos événements en Ille-et-Vilaine.
            Matériel professionnel, sécurité maximale et <strong>fun garanti</strong> — du loisir Ille-et-Vilaine à la sortie team building entreprise Fougères.
          </p>

          <p
            data-testid="hero-warning"
            className="mt-4 inline-flex items-start gap-2 text-sm md:text-base text-[#E91E63] font-bold"
          >
            <span className="text-xl leading-none">⚠️</span>
            <span>
              Activité accessible <u>UNIQUEMENT</u> sur réservation préalable à l&apos;avance.
            </span>
          </p>

          <div className="mt-8 md:mt-10 flex flex-wrap gap-3 md:gap-4">
            <PreReservationDialog
              trigger={
                <button
                  data-testid="hero-reserve-button"
                  className="group inline-flex items-center gap-3 px-6 md:px-8 py-4 bg-[#E91E63] text-white font-bold uppercase text-sm md:text-base tracking-wider hover:-translate-y-1 hover:shadow-[0_10px_0_-2px_#0a0a0a] transition-all"
                >
                  Pré-réservation en ligne
                  <ArrowRight
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                    strokeWidth={2.5}
                  />
                </button>
              }
            />
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-3 px-6 md:px-8 py-4 border-2 border-black text-black font-bold uppercase text-sm md:text-base tracking-wider hover:bg-black hover:text-white transition-colors"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              {PHONE}
            </a>
          </div>

          <p className="mt-3 text-xs text-gray-500 font-mono uppercase tracking-wider">
            Uniquement sur réservation préalable
          </p>

          {/* Trust strip */}
          <div className="mt-8 md:mt-10 grid grid-cols-3 gap-4 max-w-xl pb-16 lg:pb-0">
            {[
              { icon: Trees, label: "6 terrains", sub: "en forêt" },
              { icon: ShieldCheck, label: "Sécurité", sub: "filets pro" },
              { icon: MapPin, label: "2 sites", sub: "en Ille-et-Vilaine" },
            ].map((t) => (
              <div
                key={t.label}
                className="border-l-2 border-[#E91E63] pl-3"
                data-testid={`trust-${t.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <t.icon className="w-5 h-5 text-[#E91E63] mb-1.5" strokeWidth={2.5} />
                <p className="font-display font-bold text-lg uppercase leading-none">
                  {t.label}
                </p>
                <p className="text-xs text-gray-600 mt-1">{t.sub}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right: image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] md:aspect-[3/4] lg:aspect-[4/5] w-full overflow-hidden bg-black">
            <video
              src={HERO_VIDEO_URL}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-label="Vidéo Paintball Aventures 35 — terrain paintball forêt Fougères Ille-et-Vilaine"
              data-testid="hero-video"
              className="absolute inset-0 w-full h-full object-contain bg-black"
            />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 text-white">
              <p className="font-display text-2xl md:text-3xl font-bold uppercase leading-tight">
                +20 ans d&apos;expérience<br />
                au cœur de la Bretagne
              </p>
            </div>
          </div>
          {/* Pink corner accent */}
          <div className="absolute -bottom-4 -right-4 w-24 h-24 md:w-32 md:h-32 bg-[#E91E63] -z-10 hidden md:block" />
        </motion.div>
      </div>
    </section>
  );
}
